import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/brand";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";

const In = ({ label, value, onChange, testid, area }) => (
  <div><label className="label-cr">{label}</label>{area ? <textarea data-testid={testid} rows={3} className="input-cr" value={value ?? ""} onChange={(e) => onChange(e.target.value)} /> : <input data-testid={testid} className="input-cr" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />}</div>
);

const ConfigForm = () => {
  const { loadConfig } = useAuth();
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/admin/config").then((r) => setC(r.data)); }, []);
  if (!c) return <Skeleton className="h-64 bg-cr-elevated" />;
  const set = (sec, k, v) => setC({ ...c, [sec]: { ...c[sec], [k]: v } });
  const setBonus = (k, v) => set("landing", "bonus", { ...c.landing.bonus, [k]: v });
  const num = (v) => Number(String(v).replace(/[^\d.]/g, "")) || 0;
  const save = async () => {
    try {
      const body = { landing: c.landing, footer: c.footer, support: c.support, compliance: c.compliance, sports_nav: c.sports_nav,
        limits: { ...c.limits, deposit_min: num(c.limits.deposit_min), deposit_max: num(c.limits.deposit_max), withdraw_min: num(c.limits.withdraw_min), withdraw_max: num(c.limits.withdraw_max) } };
      body.landing.bonus.percent = num(body.landing.bonus.percent); body.landing.bonus.cap = num(body.landing.bonus.cap);
      await api.put("/admin/config", body); await loadConfig(); toast.success("Configuration saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="card-cr grid gap-4 p-5 md:grid-cols-2">
        <p className="font-semibold md:col-span-2">Landing page</p>
        <In testid="cfg-hero-title" label="Hero headline" value={c.landing.hero_title} onChange={(v) => set("landing", "hero_title", v)} />
        <In testid="cfg-hero-subtitle" label="Hero description" value={c.landing.hero_subtitle} onChange={(v) => set("landing", "hero_subtitle", v)} area />
        <In testid="cfg-bonus-percent" label="Bonus percent" value={c.landing.bonus.percent} onChange={(v) => setBonus("percent", v)} />
        <In testid="cfg-bonus-cap" label="Bonus cap (₹)" value={c.landing.bonus.cap} onChange={(v) => setBonus("cap", v)} />
        <In testid="cfg-bonus-eligibility" label="Bonus eligibility" value={c.landing.bonus.eligibility} onChange={(v) => setBonus("eligibility", v)} area />
        <In testid="cfg-bonus-terms" label="Bonus terms" value={c.landing.bonus.terms} onChange={(v) => setBonus("terms", v)} area />
      </div>
      <div className="card-cr grid gap-4 p-5 md:grid-cols-4">
        <p className="font-semibold md:col-span-4">Transaction limits</p>
        {["deposit_min", "deposit_max", "withdraw_min", "withdraw_max"].map((k) => <In key={k} testid={`cfg-${k}`} label={k.replace("_", " ")} value={c.limits[k]} onChange={(v) => set("limits", k, v)} />)}
      </div>
      <div className="card-cr grid gap-4 p-5 md:grid-cols-2">
        <p className="font-semibold md:col-span-2">Compliance, footer & navigation</p>
        <In testid="cfg-license" label="Licensing text (only verified info)" value={c.compliance.license_text} onChange={(v) => set("compliance", "license_text", v)} area />
        <In testid="cfg-blocked" label="Blocked countries (comma separated)" value={c.compliance.blocked_countries.join(", ")} onChange={(v) => set("compliance", "blocked_countries", v.split(",").map((x) => x.trim()).filter(Boolean))} area />
        <In testid="cfg-footer-about" label="Footer about text" value={c.footer.about} onChange={(v) => set("footer", "about", v)} area />
        <In testid="cfg-support-email" label="Support email" value={c.support.email} onChange={(v) => set("support", "email", v)} />
        <In testid="cfg-sports-nav" label="Sports categories (comma separated)" value={c.sports_nav.join(", ")} onChange={(v) => setC({ ...c, sports_nav: v.split(",").map((x) => x.trim()).filter(Boolean) })} />
      </div>
      <button data-testid="cfg-save-btn" onClick={save} className="btn-gold">Save configuration</button>
    </div>
  );
};

const Methods = () => {
  const [ms, setMs] = useState(null);
  const load = () => api.get("/admin/content/payment-methods").then((r) => setMs(r.data));
  useEffect(() => { load(); }, []);
  const upd = async (m, patch) => { try { await api.put(`/admin/content/payment-methods/${m.id}`, patch); toast.success("Updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!ms) return <Skeleton className="h-40 bg-cr-elevated" />;
  return (
    <div className="space-y-3">{ms.map((m) => (
      <div key={m.id} data-testid={`admin-method-${m.code}`} className="card-cr flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="flex-1"><p className="font-semibold">{m.name} <span className="text-xs uppercase text-cr-muted">({m.kind})</span></p><p className="text-xs text-cr-muted">{m.description}</p></div>
        <label className="flex items-center gap-2 text-xs">Fee %<input data-testid={`method-fee-${m.code}`} defaultValue={m.fee_pct} onBlur={(e) => Number(e.target.value) !== m.fee_pct && upd(m, { fee_pct: Number(e.target.value) || 0 })} className="input-cr w-20 py-1.5" /></label>
        <label className="flex items-center gap-2 text-xs">Flat ₹<input data-testid={`method-flat-${m.code}`} defaultValue={m.fee_flat} onBlur={(e) => Number(e.target.value) !== m.fee_flat && upd(m, { fee_flat: Number(e.target.value) || 0 })} className="input-cr w-20 py-1.5" /></label>
        <Switch data-testid={`method-toggle-${m.code}`} checked={m.enabled} onCheckedChange={(v) => upd(m, { enabled: v })} />
      </div>))}</div>
  );
};

const EMPTY_PROMO = { title: "", subtitle: "", eligibility: "", terms: "", cta_link: "/promotions", cta_label: "Learn More", image: "/brand/crown.jpg", expires_at: "" };

const Promos = () => {
  const [ps, setPs] = useState(null);
  const [f, setF] = useState(EMPTY_PROMO);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/content/promotions").then((r) => setPs(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const body = { ...f, expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null };
      editing ? await api.put(`/admin/content/promotions/${editing}`, body) : await api.post("/admin/content/promotions", body);
      toast.success("Promotion saved"); setF(EMPTY_PROMO); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/content/promotions/${id}`); load(); };
  const toggle = async (p) => { await api.put(`/admin/content/promotions/${p.id}`, { active: !p.active }); load(); };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-3">{!ps ? <Skeleton className="h-40 bg-cr-elevated" /> : ps.map((p) => (
        <div key={p.id} data-testid={`admin-promo-${p.id}`} className="card-cr flex items-center gap-3 p-4">
          <div className="flex-1"><p className="font-semibold">{p.title}</p><p className="text-xs text-cr-muted">{p.subtitle}</p></div>
          <Switch data-testid={`promo-toggle-${p.id}`} checked={p.active} onCheckedChange={() => toggle(p)} />
          <button data-testid={`promo-edit-${p.id}`} onClick={() => { setEditing(p.id); setF({ ...EMPTY_PROMO, ...p, expires_at: p.expires_at?.slice(0, 10) || "" }); }} className="text-xs text-cr-gold">Edit</button>
          <button data-testid={`promo-delete-${p.id}`} onClick={() => del(p.id)} className="text-xs text-cr-danger">Delete</button>
        </div>))}</div>
      <div className="card-cr space-y-3 p-5">
        <p className="font-semibold">{editing ? "Edit promotion" : "New promotion"}</p>
        {["title", "subtitle", "eligibility", "terms", "cta_link", "cta_label", "image"].map((k) => <In key={k} testid={`promo-${k}`} label={k.replace("_", " ")} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} />)}
        <div><label className="label-cr">Expires</label><input type="date" data-testid="promo-expires" className="input-cr" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} /></div>
        <div className="flex gap-2"><button data-testid="promo-save-btn" disabled={!f.title} onClick={save} className="btn-gold">Save</button>{editing && <button onClick={() => { setEditing(null); setF(EMPTY_PROMO); }} className="btn-outline">Cancel</button>}</div>
      </div>
    </div>
  );
};

const Games = () => {
  const [gs, setGs] = useState(null);
  const load = () => api.get("/admin/content/games").then((r) => setGs(r.data));
  useEffect(() => { load(); }, []);
  const upd = async (g, patch) => { try { await api.put(`/admin/content/games/${g.id}`, patch); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!gs) return <Skeleton className="h-40 bg-cr-elevated" />;
  return (
    <div className="space-y-2">{gs.map((g) => (
      <div key={g.id} data-testid={`admin-game-${g.id}`} className="card-cr flex flex-col gap-2 p-3 sm:flex-row sm:items-center">
        <img src={g.image} alt="" className="h-10 w-14 rounded object-cover" />
        <input defaultValue={g.name} onBlur={(e) => e.target.value !== g.name && upd(g, { name: e.target.value })} className="input-cr py-1.5 sm:w-56" data-testid={`game-name-${g.id}`} />
        <input defaultValue={g.provider} onBlur={(e) => e.target.value !== g.provider && upd(g, { provider: e.target.value })} className="input-cr py-1.5 sm:w-56" data-testid={`game-provider-${g.id}`} />
        <span className="flex-1 text-xs capitalize text-cr-muted">{g.section} · {g.category} · {g.tags.join(", ")}</span>
        <Switch data-testid={`game-toggle-${g.id}`} checked={g.active} onCheckedChange={(v) => upd(g, { active: v })} />
      </div>))}</div>
  );
};

const TABS = [["config", "Site & Limits", ConfigForm], ["methods", "Payment Methods", Methods], ["promos", "Promotions", Promos], ["games", "Game Catalogue", Games]];

export default function AdminContent() {
  const [tab, setTab] = useState("config");
  const Comp = TABS.find((t) => t[0] === tab)[2];
  return (
    <div data-testid="admin-content-page">
      <PageTitle title="Content & Configuration" subtitle="All changes are recorded in the audit log." />
      <div className="mb-6 flex gap-6 overflow-x-auto border-b border-cr-line">
        {TABS.map(([k, l]) => <button key={k} data-testid={`admin-content-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px shrink-0 border-b-2 pb-3 text-sm ${tab === k ? "border-cr-gold text-cr-gold" : "border-transparent"}`}>{l}</button>)}
      </div>
      <Comp />
    </div>
  );
}
